import { useState, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import FlashOnIcon from '@mui/icons-material/FlashOnOutlined';
import SchoolIcon from '@mui/icons-material/SchoolOutlined';
import CampaignIcon from '@mui/icons-material/CampaignOutlined';
import PeopleIcon from '@mui/icons-material/PeopleAltOutlined';
import { useAuth } from '../context/authContext';

interface QuickCreateItem {
  /** Full menu text, shown as-is (not auto-prefixed with "New") — Quick
   * Lead needs to read exactly "Quick Lead", not "New Quick Lead". */
  label: string;
  path: string;
  icon: typeof ContactsIcon;
  visible: boolean;
  /** Router state passed on navigation — each destination page looks for
   * its own flag (LeadsListPage/StudentsListPage already check
   * `quickCreate`; Quick Lead needs its own distinct `openQuickLead` flag
   * so LeadsListPage can tell the two apart and open the right dialog). */
  state?: Record<string, unknown>;
}

export const QuickCreateButton = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const items: QuickCreateItem[] = [
    {
      label: 'New Lead',
      path: '/leads',
      icon: ContactsIcon,
      visible: hasPermission('leads', 'view'),
      state: { quickCreate: true },
    },
    // Placed directly after New Lead (spec §9) — same permission as the
    // Leads list page's own "Quick Lead" toolbar button gates on
    // (leads:create), so a role that can't create leads never sees this
    // either, on the menu or via a direct navigation. Reuses the exact
    // same existing QuickLeadDialog component — never a second/duplicate
    // Quick Lead implementation.
    {
      label: 'Quick Lead',
      path: '/leads',
      icon: FlashOnIcon,
      visible: hasPermission('leads', 'create'),
      state: { openQuickLead: true },
    },
    {
      label: 'New Student',
      path: '/students',
      icon: SchoolIcon,
      visible: hasPermission('students', 'view'),
      state: { quickCreate: true },
    },
    {
      label: 'New Campaign',
      path: '/campaigns',
      icon: CampaignIcon,
      visible: hasPermission('email', 'create'),
      state: { quickCreate: true },
    },
    {
      label: 'New User',
      path: '/users',
      icon: PeopleIcon,
      visible: hasPermission('users', 'view'),
      state: { quickCreate: true },
    },
  ].filter((item) => item.visible);

  if (items.length === 0) return null;

  const handleSelect = (item: QuickCreateItem) => {
    setAnchorEl(null);
    navigate(item.path, { state: item.state ?? null });
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={(e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
      >
        New
      </Button>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
        {items.map((item) => (
          <MenuItem key={item.label} onClick={() => handleSelect(item)}>
            <ListItemIcon>
              <item.icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
